<template>
  <div class="sam-select-list">
    <div class="list__header">
      <div
        v-if="title"
        class="list__title"
      >
        {{ title }}
      </div>
      <div
        v-if="hasSearch"
        class="list__search"
      >
        <SamSearch
          ref="searchInput"
          v-model="searchInput"
          placeholder="Search"
          @clear="searchInput = ''"
        />
      </div>
    </div>
    <div
      class="list__content"
      @scroll="handleScroll"
    >
      <ul ref="optionsListWrapper">
        <template v-if="!filteredItems.length && $slots['no-data']">
          <slot name="no-data" />
        </template>

        <SamTextListItem
          v-if="shouldShowSelectAll"
          :item="selectAllItem"
          :class="['list__item', { 'is-selected': allItemsSelected }]"
          @click.prevent="selectAll"
        >
          <template v-slot:prepend>
            <SamCheckbox :data="allItemsSelected" />
          </template>
        </SamTextListItem>

        <SamTextListItem
          v-for="(item, index) in filteredItems"
          :key="`select_list_item__${index}`"
          :item="item"
          :show-custom-content="getShowCustomContent(item)"
          :class="['list__item', { 'is-selected': checkItemSelection(item) }]"
          :bablic-exclude="dontTranslateItems"
          @click.prevent="selectItem(item, $event)"
        >
          <template v-slot:prepend>
            <SamCheckbox
              v-if="multiple"
              v-bind="getCheckboxProps(item)"
              :data="checkItemSelection(item)"
            />
          </template>
        </SamTextListItem>

        <template v-if="loading">
          <SamTextListItem
            v-for="index in 5"
            :key="index"
            loading
          />
        </template>
      </ul>
    </div>
    <div
      v-if="hasSelectionFooter"
      class="list__footer"
    >
      <span class="list__sum">{{ footerText }}</span>
      <SamButton
        sam-type="text"
        label="Clear"
        @click.stop="clearSelection"
      />
    </div>
  </div>
</template>

<script>
import { debounce, objectHasOwnProperties } from '../../../utils/utils';
import SamSearch from '../SamSearch/SamSearch.vue';
import SamCheckbox from '../SamCheckbox/SamCheckbox.vue';
import SamButton from '../SamButton/SamButton.vue';
import SamTextListItem from '../SamTextList/SamTextListItem.vue';

const FOOTER_TEXT = {
  SINGLE: 'option selected',
  MULTIPLE: 'options selected',
};

export default {
  name: 'SamSelectList',
  components: {
    SamTextListItem,
    SamButton,
    SamCheckbox,
    SamSearch,
  },
  props: {
    /**
     * The items to be shown on the list. They must follow a specific structure.
     * @type TextListItem[]
     */
    items: {
      type: Array,
      required: true,
      default: () => [],
      validator: (val) => {
        return val?.every?.((item) => objectHasOwnProperties(item, 'label', 'value'));
      },
    },
    /**
     * Flag to show if it should display the selection footer. (only when multiple select is active)
     */
    hasClear: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * With select all option
     * @type Boolean
     */
    hasSelectAll: {
      type: Boolean,
      default: false,
      required: false,
    },
    /**
     * If it allows multiple selection
     */
    multiple: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Title of the select
     */
    title: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * Value of the select
     * @model
     */
    // eslint-disable-next-line vue/require-prop-types
    value: {
      required: false,
      default: null,
    },
    /**
     * If you wish to exclude items text from translation
     */
    dontTranslateItems: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * This value is used to specify debounce time used when search is set to search on input change
     * @values number
     */
    debounceTime: {
      type: Number,
      required: false,
      default: 0,
    },
    /**
     * Whether to set the list in a loading state
     * @values true, false
     */
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Whether the list should be filterable
     * @type Boolean
     */
    filterable: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * Whether to hide search when items are less than 5
     * @type Boolean
     */
    autoHideSearch: {
      type: Boolean,
      required: false,
      default: true,
    },
    /*
     * if the search input should be autofocused
     * @type Boolean
     */
    autoFocusSearch: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: () => ({
    selection: null,
    searchInput: '',
    selectAllItem: { label: 'Select all', value: '::all::' },
    debouncedSearch: undefined,
  }),
  computed: {
    hasSearch() {
      if (!this.autoHideSearch) return true;
      return this.items.length > 5;
    },
    shouldShowSelectAll() {
      return this.hasSelectAll && this.multiple && this.filteredItems.length > 1;
    },
    allItemsSelected() {
      return (this.selection ?? []).length === this.items.length;
    },
    hasSelectionFooter() {
      return this.hasClear && this.multiple && this.selection?.length;
    },
    footerText() {
      return `${this.selection.length} ${
        this.selection.length > 1 ? FOOTER_TEXT.MULTIPLE : FOOTER_TEXT.SINGLE
      }`;
    },
    filteredItems() {
      if (!this.searchInput || !this.filterable) return this.items;
      return this.items.filter((item) =>
        `${item.label}`.toLowerCase().includes(this.searchInput.toLowerCase())
      );
    },
  },
  watch: {
    multiple: {
      handler() {
        this.clearSelection();
      },
      immediate: true,
    },
    value(val) {
      this.selection = val;
    },
    searchInput() {
      this.debouncedSearch();
    },
  },
  created() {
    this.debouncedSearch = debounce(this.emitSearch, this.debounceTime);
    this.selection = this.multiple ? [...(this.value ?? [])] : this.value;
    this.$watch('selection', (updatedSelection) => {
      /**
       * The updated bound model
       * @event input
       * @param updatedSelection The updated selection
       */
      this.$emit('input', updatedSelection);
    });
  },
  mounted() {
    if (this.autoFocusSearch) {
      setTimeout(this.focusInput);
    }
  },
  beforeDestroy() {
    if (this.debouncedSearch) {
      this.debouncedSearch = null;
    }
  },
  methods: {
    emitSearch() {
      this.$emit('search', this.searchInput);
    },
    focusInput() {
      const input = this.$refs?.searchInput?.$refs?.input;
      if (input) input.focus();
    },
    selectAll() {
      if (!this.allItemsSelected) {
        this.selection = this.filteredItems.map((v) => v.value);
      } else {
        this.selection = [];
      }
    },
    selectItem(item, event) {
      /**
       * Event triggered when selecting an item
       * @event item-click
       * @param item The selected item
       */
      this.$emit('item-click', item);

      if (!this.multiple) {
        this.selection = item.value;
        return;
      }

      // If multiple, find element in array and do according action
      const itemIndex = this.selection.findIndex((selectionValue) => selectionValue === item.value);
      if (itemIndex < 0) this.selection.push(item.value);
      else {
        this.selection.splice(itemIndex, 1);
        // blur option if we are removing it
        setTimeout(() => event?.target?.blur());
      }
    },
    clearSelection() {
      this.selection = this.multiple ? [] : null;
    },
    checkItemSelection(item) {
      if (this.multiple) return this.selection?.includes(item.value) ?? false;
      return this.selection === item.value;
    },
    handleScroll(event) {
      const {
        target: { scrollHeight, scrollTop },
      } = event;
      if (scrollTop < scrollHeight * 0.05) {
        this.$emit('on-top');
      } else if (scrollTop > scrollHeight * 0.75) {
        this.$emit('on-bottom');
      }
    },
    getCheckboxProps(item) {
      if (this.getShowCustomContent(item)) {
        return {};
      }

      return {
        label: item.label,
        caption: item.caption,
      };
    },
    getShowCustomContent(item) {
      return item.showCustomContent ?? true;
    },
  },
};
</script>

<style scoped lang="scss">
.list__search {
  @extend .py-3, .px-4;
  border-bottom: $size--0--fourth solid $neutral--40;
}

.list__footer {
  @extend .p-4, .d-flex, .between-xs, .flex-center;
  background-color: $neutral--20;

  .list__sum {
    @extend .text--subtitle;
  }
}

.list__checkbox {
  pointer-events: none;
}

.list__content {
  max-height: calc($size--14 * 4 + $size--7); // 4 list items + half
  overflow-y: auto;
}

.list__title {
  @extend .text--subtitle, .px-4, .pt-4, .pb-2;
}

.list__item:not(.is-disabled) {
  &:hover {
    background-color: $informative--surface;
    color: $informative--hover;
  }

  &.is-selected {
    background-color: $informative--surface;
  }

  &:focus {
    outline: $size--0--fourth solid $informative--main;
    outline-offset: -#{$size--0--fourth};

    &:after {
      display: none;
    }
  }

  ::v-deep(.c-Checkbox__label span) {
    @extend .p-0;
  }
}
</style>
